import { z } from 'zod';

/**
 * Extract digits from phone string
 */
export function getPhoneDigits(phone: string): string {
  return phone.replace(/\D/g, '');
}

/**
 * US Phone Number Validation
 * Accepts: (XXX) XXX-XXXX, XXX-XXX-XXXX, XXXXXXXXXX, +1XXXXXXXXXX, etc.
 */
export const phoneSchema = z.string()
  .trim()
  .min(1, 'Phone number is required')
  .refine((val) => {
    const digits = getPhoneDigits(val);
    if (digits.length === 0) return false;
    if (digits.length < 10) return false;
    if (digits.length > 11) return false;
    if (digits.length === 11 && !digits.startsWith('1')) return false;
    return true;
  }, (val) => {
    const digits = getPhoneDigits(val);
    if (digits.length === 0) {
      return { message: 'Phone number is required' };
    }
    if (digits.length < 10) {
      return { message: `Phone needs ${10 - digits.length} more digit${10 - digits.length === 1 ? '' : 's'} (currently ${digits.length})` };
    }
    if (digits.length > 11) {
      return { message: `Phone has too many digits (${digits.length}). Enter 10 digits like (281) 555-0123` };
    }
    if (digits.length === 11 && !digits.startsWith('1')) {
      return { message: 'For 11-digit numbers, must start with country code 1' };
    }
    return { message: 'Enter a valid phone, e.g. (281) 555-0123' };
  });

/**
 * Format phone number to (XXX) XXX-XXXX format for consistency
 */
export function formatPhoneNumber(phone: string): string {
  const digits = getPhoneDigits(phone);
  // Get last 10 digits (remove country code if present)
  const last10 = digits.slice(-10);
  if (last10.length === 10) {
    return `(${last10.slice(0, 3)}) ${last10.slice(3, 6)}-${last10.slice(6)}`;
  }
  return phone;
}

/**
 * Format phone as user types (for input onChange)
 */
export function formatPhoneAsYouType(phone: string): string {
  const digits = getPhoneDigits(phone);
  if (digits.length === 0) return '';
  if (digits.length <= 3) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  if (digits.length <= 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  // Handle 11 digits with country code
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return phone;
}

/**
 * Address schema with basic validation
 */
export const addressSchema = z.string()
  .trim()
  .min(1, 'Property address is required')
  .max(200, 'Address must be less than 200 characters');

/**
 * US State validation (2-letter code)
 */
export const stateSchema = z.string()
  .trim()
  .length(2, 'Enter 2-letter state code (e.g. TX)')
  .toUpperCase();

/**
 * US ZIP code validation (5 digits or ZIP+4)
 */
export const zipSchema = z.string()
  .trim()
  .regex(/^\d{5}(-\d{4})?$/, 'Enter a valid ZIP code (e.g. 77001)');

/**
 * Helper to get human-readable field labels for error messages
 */
export const fieldLabels: Record<string, string> = {
  referrer_name: 'Your Name',
  referrer_type: 'Your Role',
  referrer_email: 'Your Email',
  referrer_phone: 'Your Phone',
  referrer_company: 'Your Company',
  referral_name: 'Homeowner Name',
  referral_phone: 'Homeowner Phone',
  referral_email: 'Homeowner Email',
  referral_street: 'Street Address',
  referral_city: 'City',
  referral_state: 'State',
  referral_zip: 'ZIP Code',
  referral_address: 'Property Address',
  service_needed: 'Service Needed',
  additional_notes: 'Additional Notes',
  name: 'Name',
  email: 'Email',
  phone: 'Phone',
  address: 'Address',
  message: 'Message',
  service: 'Service',
};

export function getFieldLabel(fieldName: string): string {
  return fieldLabels[fieldName] || fieldName.replace(/_/g, ' ');
}
